<template>
  <div>
    <v-card-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        append-icon="search"
        label="Filter"
      />
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
      :rows-per-page-items="[25,50,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr
          @click="toggleSelected(props.item)"
        >
          <td>
            {{ props.item.description }}
          </td>
          <td
            v-if="isSpecialType(props.item)"
          >
            No PDF Creation
          </td>
          <td
            v-else
          >
            {{ getPdfCreationPercent(props.item.holders) }}
          </td>
          <td>
            {{ getGrossAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ getNettoAmount(props.item.holders)|toCurrency }}
          </td>
          <td>
            {{ parseFloat(props.item.amount)|toCurrency }}
          </td>
          <td>
            {{ props.item.security_id }}
          </td>
          <td>
            {{ types()[props.item.type] }}
          </td>
          <td>
            <DateDisplay
              :date="props.item.expected_payout_date"
            />
          </td>
          <td>
            <DateDisplay
              :date="props.item.created_at"
            />
          </td>
          <td class="text-xs">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showCancelDialog(props.item)"
                >
                  <v-icon>cancel</v-icon>
                </v-btn>
              </template>
              <span>Decline</span>
            </v-tooltip>
            <v-tooltip
              v-if="props.item.security || isSpecialType(props.item) || isAutomaticCashbackPayout(props.item)"
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="primary"
                  v-on="on"
                  @click.stop="() => showDialog(props.item)"
                >
                  <v-icon>check</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>
            <v-chip
              v-else-if="!isAutomaticCashbackPayout(props.item)"
              class="ma-2"
              color="red"
              text-color="white"
            >
              Missing Security
            </v-chip>
            <v-chip
              v-if="props.item.errors"
              class="ma-2"
              color="red"
              text-color="white"
              @click.stop="toggleErrors(props.item.errors)"
            >
              Errors!
            </v-chip>
            <v-btn
              dark
              flat
              icon
              color="primary"
              @click.stop="() => downloadCsv(props.item.payout_id)"
            >
              <v-icon>download</v-icon>
            </v-btn>
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  dark
                  flat
                  icon
                  color="error"
                  v-on="on"
                  @click.stop="() => showDeleteDialog(props.item)"
                >
                  <v-icon>delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Payout</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.security && displayErrors === props.item.errors">
          <td colspan="12">
            <ErrorDetails
              :errors="props.item.errors"
            />
          </td>
        </tr>
        <tr v-if="props.item.payout_id && selected === props.item.payout_id">
          <td colspan="11">
            <PayoutDetails
              :payout-id="props.item.payout_id"
              :payout="props.item"
              :holders="props.item.holders"
              :show-holders="true"
              :show-holders-address="true"
              :show-holders-payback-information="true"
              :show-address-retry-action="false"
              :show-pdf-download-action="true"
              :allow-to-delete-holders="true"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <ApprovalsCancelDialog
      key="decline_dialog"
      :visible="isCancelDialogActive()"
      :payout="currentPayout"
      @close="hideCancelDialog()"
      @submit="onCancel()"
    />

    <ApprovalsApproveDialog
      key="approve_dialog"
      :visible="isDialogActive()"
      :payout="currentPayout"
      @close="hideDialog()"
      @submit="onApprove()"
    />

    <DeleteDialog
      key="delete_dialog"
      :visible="isDeleteDialogActive()"
      :payout="currentPayout"
      @close="hideDeleteDialog()"
      @submit="onDelete()"
    />

    <v-snackbar
      v-model="snackbarShow"
      :color="snackbarColor"
      :auto-height="true"
    >
      <ul v-if="snackbarErrors">
        <li
          v-for="snackbarError in snackbarErrors"
          :key="snackbarError.message"
        >
          {{ snackbarError.message }}
        </li>
      </ul>

      <div
        v-if="snackbarMessage"
      >
        {{ snackbarMessage }}
      </div>

      <v-btn
        color="primary"
        text
        @click="snackbarShow = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import BigNumber from 'bignumber.js';
import ApprovalsApproveDialog from './dialogs/payout/ApprovalsApproveDialog';
import PayoutDetails from '@/components/payoutsAndTaxes/payout/shared/PayoutDetails';
import ApprovalsCancelDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/ApprovalsCancelDialog';
import DeleteDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/DeleteDialog';
import ErrorDetails from '@/components/payoutsAndTaxes/payout/shared/ErrorDetails';
import DateDisplay from '@/components/payoutsAndTaxes/payout/shared/DateDisplay';
import HolderErrorMessagesDialog from '@/components/payoutsAndTaxes/payout/dialogs/payout/HolderErrorMessagesDialog';
import { downloadFromS3 } from '@/helpers/axiosS3Downloader';
import { downloadCsvFile } from '@/helpers';

export default {
  name: 'FinalCheckApproval',
  components: {
    ErrorDetails,
    PayoutDetails,
    ApprovalsApproveDialog,
    ApprovalsCancelDialog,
    DeleteDialog,
    DateDisplay,
  },
  data() {
    return {
      snackbarShow: false,
      snackbarMessage: '',
      snackbarColor: 'error',
      snackbarErrors: [],
      selected: 0,
      displayErrors: {},
      currentPayout: {},
      isLoading: false,
      approvalDialog: false,
      cancelDialog: false,
      deleteDialog: false,
      pagination: {
        rowsPerPage: 25,
      },
      search: '',
      headers: [
        { text: 'Payout', value: 'description' },
        { text: 'PDFs (%)', value: 'pdf_creation_complete_percent' },
        { text: 'Amount (Gross)', value: 'gross_amount' },
        { text: 'Amount (After taxes)', value: 'netto_amount' },
        { text: 'Amount', value: 'amount' },
        { text: 'Security ID', value: 'security_id' },
        { text: 'Type', value: 'type' },
        { text: 'Expected Payout Date', value: 'expected_payout_date' },
        { text: 'Created At', value: 'created_at' },
        { text: 'Actions', value: 'actions' },
      ],
    };
  },
  computed: {
    payoutFinalCheckPendingApprovals() {
      return this.$store.getters.getPayoutsFinalCheckApprovalPending;
    },
    filteredItems() {
      const { search } = this;

      return search.length < 1 ?
        this.payoutFinalCheckPendingApprovals :
        this.payoutFinalCheckPendingApprovals.filter((item) => {
          let found = false;
          Object.keys(item)
            .map((key) => {
              if (item[key] && String(item[key]).toLowerCase().indexOf(search.toLowerCase()) > -1) {
                found = true;
              }

              return key;
            });

          return found;
        });
    },
  },
  methods: {
    types() {
      const data = this.$store.getters.getPayoutsCreationTypes;
      if (!data || !data.payout || !data.payout.types) {
        return {};
      }

      return data.payout.types;
    },
    getGrossAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }
      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.gross_amount.total || 0,
        initialSumValue,
      );
    },
    getNettoAmount(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }
      const initialSumValue = 0;

      return holders.reduce(
        (sum, holder) => sum + holder.payback_information.net_amount.total || 0,
        initialSumValue,
      );
    },
    getPdfCreationPercent(holders) {
      if (!holders) {
        return 'Expand to calculate';
      }

      const initialValue = 0;

      const countComplete = holders.reduce(
        (sum, holder) => sum + ((holder.pdf)
          ? 1
          : 0),
        initialValue,
      );

      const percentage = new BigNumber(100).dividedBy(holders.length).multipliedBy(countComplete).toFixed(2);

      return `${percentage}%`;
    },
    onApprove() {
      const payout = this.currentPayout;

      this.$store.dispatch('callPayoutFinalCheckApprovalApprove', { payoutId: payout.payout_id, approve: true })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout approved!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onCancel() {
      const payout = this.currentPayout;

      this.$store.dispatch('callPayoutFinalCheckApprovalApprove', { payoutId: payout.payout_id, approve: false })
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout PDFs declined!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    onDelete() {
      const payout = this.currentPayout;

      this.$store.dispatch('callPayoutDelete', payout.payout_id)
        .then(() => {
          this.snackbarShow = true;
          this.snackbarErrors = [];
          this.snackbarMessage = 'Payout Deleted!';
          this.snackbarColor = 'success';
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    isDeleteDialogActive() {
      return this.deleteDialog === true;
    },
    showDeleteDialog(payout) {
      this.currentPayout = payout;
      this.deleteDialog = true;
      this.$forceUpdate();
    },
    hideDeleteDialog() {
      this.deleteDialog = false;
      this.$forceUpdate();
    },
    isDialogActive() {
      return this.approvalDialog === true;
    },
    showDialog(payout) {
      this.currentPayout = payout;
      this.approvalDialog = true;
      this.$forceUpdate();
    },
    hideDialog() {
      this.approvalDialog = false;
      this.$forceUpdate();
    },
    toggleSelected(payout) {
      this.selected = this.selected === payout.payout_id ? null : payout.payout_id;
      this.loadPayoutHolders(payout);
    },
    isAutomaticCashbackPayout(payout) {
      return payout.type === 'cashback_v2';
    },
    loadPayoutHolders(payout) {
      if (payout.holders && payout.holders.length !== 0) {
        return;
      }

      this.$store.dispatch('fetchPayoutDataGetHolders', {
        origin: 'finalCheckApproval',
        payoutId: payout.payout_id,
      })
        .then(() => {
          this.$forceUpdate();
        })
        .catch((error) => {
          this.snackbarShow = true;
          this.snackbarErrors = (error.response && error.response.data) ? error.response.data : 'Error';
          this.snackbarMessage = '';
          this.snackbarColor = 'error';
        });
    },
    toggleErrors(errors) {
      this.displayErrors = this.displayErrors === errors ? null : errors;
    },
    downloadCsv(payoutId) {
      this.$store
        .dispatch('fetchPayoutBankTransferDownloadCsv', payoutId)
        .then((response) => {
          downloadFromS3(response.data).then((data) => {
            downloadCsvFile(data, response.data.fileName);
          });
        });
    },
    isCancelDialogActive() {
      return this.cancelDialog === true;
    },
    showCancelDialog(payout) {
      this.currentPayout = payout;
      this.cancelDialog = true;
      this.$forceUpdate();
    },
    hideCancelDialog() {
      this.cancelDialog = false;
      this.$forceUpdate();
    },
    isSpecialType(payout) {
      return [
        'ex_gratia',
        'starting_balance',
        'cashback',
        'tell_a_friend',
      ].includes(payout.type);
    },
  },
};
</script>
<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/components/payoutsAndTaxes/payout/FinalCheckApproval.vue